import { vec3 } from 'gl-matrix';
import { Subject } from 'rxjs';

import { TimedMovement } from './TimedMovement';
import { BoundingDimensions } from '../util';
import { NavigationSystem, PathNode, PathSegment } from '../navigation-mesh';
import { SpatialQueryGrid, sampleFootprintFloorHeight } from '../spatial';
import { PhysicsBody } from '../physics';
import { NavigationBehaviour } from './behaviours/NavigationBehaviour';

export type { NavigationBehaviour };

export class NavigationAwareObject {
  private behaviours: Set<NavigationBehaviour>;
  public movement: TimedMovement<MovementNode>;
  public pathChanged: Subject<PathSegment[]>;
  public physicsBody?: PhysicsBody;

  constructor(
    public navigationObject: NavigationObject,
    public navigationData: NavigationData,
    speed: number,
  ) {
    this.behaviours = new Set<NavigationBehaviour>();
    // Initial node lookup is a one-shot at spawn — a wide radius is fine
    // (XZ-containment filter inside getPathNode picks the right node by
    // closest Y mid-point). 10m used to be the default but is far too narrow
    // when the spawn position comes from a freecam that may be hundreds of
    // metres off the actual ground at the same XZ.
    const initialNode =
      this.getPathNode(this.navigationObject.position, 50) ??
      this.getPathNode(this.navigationObject.position, 10000);
    if (!initialNode) {
      console.warn('No nav node found at spawn position', this.navigationObject.position);
    }
    this.movement = new TimedMovement<MovementNode>(
      this.navigationObject,
      {
        position: this.navigationObject.position,
        node: initialNode as PathNode,
      },
      speed,
    );

    this.pathChanged = new Subject<PathSegment[]>();

    this.movement.onStep.subscribe((mv) => {
      let pos = this.navigationObject.position;
      const maxStep = this.navigationData.navigation.maxNeighbourStepHeight;
      const grid = this.navigationData.spatialGrid;
      // Footprint sample — same reasoning as PlayerControlBehaviour. NPCs
      // crossing seams between two flush-but-not-welded floors would otherwise
      // fall through to whatever lies below.
      const radius = this.navigationData.selfAgent?.radius ?? 0;
      const surfaceHeight = grid
        ? sampleFootprintFloorHeight(
            grid,
            pos[0] as number,
            pos[1] as number,
            pos[2] as number,
            radius,
            isFinite(maxStep) ? maxStep : 1,
          )
        : null;
      if (surfaceHeight !== null && surfaceHeight !== undefined) {
        if (this.physicsBody) {
          // Same fall-clamp rule as PlayerControlBehaviour.applyMove: while
          // grounded, track normally; while falling, only RAISE so the
          // floor clamp pins the highest surface seen during the trajectory.
          const existing = this.physicsBody.surfaceHeight;
          if (this.physicsBody.grounded || existing === null || surfaceHeight >= existing) {
            this.physicsBody.surfaceHeight = surfaceHeight;
            this.physicsBody.minFloorHeight = surfaceHeight;
          }
        } else {
          this.navigationObject.position[1] = surfaceHeight;
        }
      }
    });
  }

  public getPathNode(position: vec3 | number[], heightRange: number): PathNode | undefined {
    // Find the closest nav node containing this XZ position within Y range
    let candidates = this.navigationData.navigation.database.getObjectsInRange(
      vec3.fromValues(position[0] as number, position[1] as number, position[2] as number),
      heightRange,
    );

    let best: PathNode | undefined;
    let bestYDist = Infinity;

    for (let node of candidates) {
      let bb = node.getBoundingBox();
      // Check XZ containment
      if (
        position[0] >= bb.min[0] &&
        position[0] <= bb.max[0] &&
        position[2] >= bb.min[2] &&
        position[2] <= bb.max[2]
      ) {
        let nodeY = (((bb.min[1] as number) + bb.max[1]) as number) / 2;
        let yDist = Math.abs(nodeY - (position[1] as number));
        if (yDist < bestYDist) {
          bestYDist = yDist;
          best = node;
        }
      }
    }

    return best;
  }

  public bind(behaviour: NavigationBehaviour) {
    behaviour.attach(this);
    this.behaviours.add(behaviour);
  }

  public unbind(behaviour: NavigationBehaviour) {
    behaviour.detach(this);
    this.behaviours.delete(behaviour);
  }

  public navigateToPosition(position: vec3 | number[], options: NavigateOptions = {}) {
    let node = this.getPathNode(position, 25);
    if (!node) {
      // Nothing to navigate to
      return;
    }

    let route = this.navigationData.navigation.pathFinder.findPath(
      this.movement.currentNode!.node,
      node,
      this.navigationObject.position,
      position,
      options.maxRadius != null ? { maxRadius: options.maxRadius } : {},
    );

    if (route.solved) {
      let path = this.navigationData.navigation.pathFinder.optimizeMovementPath(
        this.movement.currentNode!.node,
        route,
      );
      this.pathChanged.next(path);
      this.movement.setPath(path);
    }
  }

  public navigateToNode(
    targetNode: PathNode,
    position: vec3 | number[],
    options: NavigateOptions = {},
  ) {
    let route = this.navigationData.navigation.pathFinder.findPath(
      this.movement.currentNode!.node,
      targetNode,
      this.navigationObject.position,
      position,
      options.maxRadius != null ? { maxRadius: options.maxRadius } : {},
    );

    if (route.solved) {
      let path = this.navigationData.navigation.pathFinder.optimizeMovementPath(
        this.movement.currentNode!.node,
        route,
      );
      this.pathChanged.next(path);
      this.movement.setPath(path);
    } else {
      console.warn('no path', this.movement.currentNode!.node, targetNode);
    }
  }
}

export type NavigateOptions = {
  /** Override the path finder's default search radius for this call. */
  maxRadius?: number;
};

type NavigationObject = {
  position: vec3 | number[];
  bounds?: BoundingDimensions;
};

type MovementNode = {
  position: vec3 | number[];
  node: PathNode;
};

type NavigationData = {
  navigation: NavigationSystem;
  spatialGrid: SpatialQueryGrid | null;
  /**
   * Optional dynamic-agent registry. Behaviours that move the entity
   * (player control, NPC steering) consult this to clamp movement against
   * other agents. Null when the scene doesn't track dynamic agents (unit
   * tests, headless contexts).
   */
  agentRegistry?: import('../spatial/agents/AgentRegistry').AgentRegistry | null;
  /**
   * The agent representing THIS entity, if any. Passed to registry queries
   * as `self` so we don't try to body-block against ourselves. Optional —
   * entities that don't need to body-block (cameras, debug helpers) skip it.
   */
  selfAgent?: import('../spatial/agents/Agent').Agent | null;
};
